import axios from 'axios';
import { API_ENDPOINTS } from '../config/api.config';

// Global announcement banner content, managed in Strapi (single type).
// Shown identically across all storefronts.

export type AnnouncementVariant = 'info' | 'success' | 'warning';

export interface Announcement {
  message: string;
  variant: AnnouncementVariant;
  linkLabel?: string;
  linkUrl?: string;
}

interface AnnouncementRaw {
  message?: string;
  isActive?: boolean;
  variant?: AnnouncementVariant;
  linkLabel?: string | null;
  linkUrl?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
}

const CACHE_KEY = 'announcement_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface AnnouncementCache {
  data: Announcement | null;
  timestamp: number;
}

const isWithinWindow = (raw: AnnouncementRaw): boolean => {
  const now = Date.now();
  if (raw.startsAt && now < new Date(raw.startsAt).getTime()) return false;
  if (raw.endsAt && now > new Date(raw.endsAt).getTime()) return false;
  return true;
};

const toAnnouncement = (raw: AnnouncementRaw | undefined | null): Announcement | null => {
  if (!raw) return null;
  if (!raw.isActive) return null;
  if (!raw.message || !raw.message.trim()) return null;
  if (!isWithinWindow(raw)) return null;

  return {
    message: raw.message.trim(),
    variant: raw.variant || 'info',
    linkLabel: raw.linkLabel || undefined,
    linkUrl: raw.linkUrl || undefined,
  };
};

const readCache = (): AnnouncementCache | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    return JSON.parse(cached) as AnnouncementCache;
  } catch {
    return null;
  }
};

export const announcementService = {
  /**
   * Fetch the active global announcement. Returns null when there is no active
   * announcement. Falls back to (possibly expired) cache on network errors so a
   * transient CMS blip never removes the banner mid-launch.
   */
  getAnnouncement: async (forceRefresh = false): Promise<Announcement | null> => {
    if (!forceRefresh) {
      const cache = readCache();
      if (cache && Date.now() - cache.timestamp < CACHE_TTL) {
        return cache.data;
      }
    }

    try {
      const res = await axios.get<{ data: AnnouncementRaw | null }>(
        API_ENDPOINTS.ANNOUNCEMENT
      );
      const announcement = toAnnouncement(res.data?.data);
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ data: announcement, timestamp: Date.now() })
        );
      } catch {
        // ignore storage quota / privacy-mode errors
      }
      return announcement;
    } catch (error) {
      console.error('Failed to fetch announcement', error);
      // Graceful fallback to whatever we last saw.
      const cache = readCache();
      return cache ? cache.data : null;
    }
  },
};
